import type { HuhokaAuthorizeRequest, HuhokaStatus } from './api';

/** Tenant roles that may connect or disconnect HuHoKa CRM (plan decision D4). */
export const HUHOKA_ADMIN_ROLES = ['admin', 'owner'];

export function isHuhokaAdmin(role: string | null | undefined): boolean {
  return HUHOKA_ADMIN_ROLES.includes((role ?? '').toLowerCase());
}

const AUTHORIZE_PARAMS: Array<keyof HuhokaAuthorizeRequest> = [
  'client_id',
  'redirect_uri',
  'state',
  'code_challenge',
  'code_challenge_method',
  'link_id',
  'remote_tenant_id',
  'remote_tenant_name',
];

/**
 * The approve-page query, passed to the BFF untouched. Only the contract's
 * parameters are read; anything else on the URL is ignored. Validation (client
 * id, redirect allow-list, PKCE, link id) happens server-side.
 */
export function readAuthorizeRequest(search: string | URLSearchParams): HuhokaAuthorizeRequest {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const request: HuhokaAuthorizeRequest = {};
  for (const key of AUTHORIZE_PARAMS) {
    const value = params.get(key);
    if (value !== null) {
      request[key] = value;
    }
  }
  return request;
}

/** What HuHoKa sent back to our callback page. */
export function readCallback(search: string | URLSearchParams): {
  state: string | null;
  code: string | null;
  error: string | null;
} {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  return { state: params.get('state'), code: params.get('code'), error: params.get('error') };
}

export type CardState = 'unconfigured' | 'disconnected' | 'pending' | 'connected' | 'error';

/** One rendered state per link status; DISCONNECTED and never-connected look the same. */
export function cardState(status: HuhokaStatus | undefined): CardState {
  if (!status) return 'disconnected';
  switch (status.status) {
    case 'ACTIVE':
      return 'connected';
    case 'PENDING':
      return 'pending';
    case 'ERROR':
      return 'error';
    default:
      return status.configured ? 'disconnected' : 'unconfigured';
  }
}

export const CARD_BADGE: Record<CardState, string> = {
  unconfigured: 'Not available',
  disconnected: 'Not connected',
  pending: 'Waiting for approval',
  connected: 'Connected',
  error: 'Needs attention',
};

/** One line about the client sync for the connected card. */
export function syncSummary(status: HuhokaStatus): string {
  const processed = status.syncProcessed.toLocaleString();
  switch (status.syncState) {
    case 'pending':
      return status.syncError ? `Waiting to retry: ${status.syncError}` : 'Starting...';
    case 'running':
      return `Syncing... ${processed} client${status.syncProcessed === 1 ? '' : 's'} so far`;
    case 'failed':
      return `Failed: ${status.syncError ?? 'the sync stopped.'}`;
    case 'completed':
      return 'Up to date';
    default:
      return 'Not started';
  }
}

/** "6 Oct 2026, 10:00" in the viewer's locale, or a dash. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
